import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Pencil, Copy, ArrowUp, ArrowDown, Trash2, Plus, Inbox, Check, X as XIcon, Link as LinkIcon } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { ThemedText, Button, ConfirmDialog } from '../';
import { LinkExistingSubmissionPicker } from '../LinkExistingSubmissionPicker';
import { renderTableCell } from '../../registry/renderers';

const FieldContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  font-weight: 600;
  color: ${props => props.theme.colors.text};
`;

const ErrorText = styled.div`
  color: ${props => props.theme.colors.danger};
  font-size: 14px;
  margin-top: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const TableContainer = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  overflow: hidden;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: ${props => props.theme.colors.backgroundTertiary};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const HeaderCell = styled.th`
  padding: ${props => props.theme.spacing.md};
  text-align: left;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  font-size: 14px;
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${props => props.theme.colors.backgroundTertiary}50;
  }
`;

const TableCell = styled.td`
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
  font-size: 14px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  align-items: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  padding: ${props => props.theme.spacing.xs};
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.backgroundTertiary};
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  ${props => props.variant === 'danger' && `
    &:hover:not(:disabled) {
      background-color: ${props.theme.colors.danger}20;
    }
  `}
`;

const EmptyState = styled.div`
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  color: ${props => props.theme.colors.muted};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const AddInstanceButton = styled(Button)`
  flex: 1;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
`;

const LinkedBadge = styled.span`
  display: inline-block;
  background-color: ${props => props.theme.colors.primaryLight || '#e0e7ff'};
  color: ${props => props.theme.colors.primary};
  font-size: 11px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: ${props => props.theme.borderRadius.sm};
  margin-left: 6px;
  vertical-align: middle;
`;

const InstanceCounter = styled.div`
  text-align: center;
  color: ${props => props.theme.colors.muted};
  font-size: 14px;
  margin-top: ${props => props.theme.spacing.sm};
`;

/**
 * Renders a field value for display in the table (registry-driven)
 */
const renderFieldValue = (instance, field) => {
  if (!field || !field.id) {
    return <span style={{ fontStyle: 'italic', opacity: 0.5 }}>-</span>;
  }

  const value = instance[field.id];

  if (value === null || value === undefined || value === '') {
    return <span style={{ fontStyle: 'italic', opacity: 0.5 }}>-</span>;
  }

  // Boolean fields render as icons in this compact table context
  if (field.type === 'toggle' || field.type === 'checkbox') {
    return value ? <Check size={16} style={{ verticalAlign: 'middle' }} /> : <XIcon size={16} style={{ verticalAlign: 'middle' }} />;
  }

  const text = renderTableCell(field, value, {});
  return text.length > 50 ? `${text.substring(0, 50)}...` : text;
};

/**
 * Derives a meaningful label for an instance from its first non-empty text field value.
 * Falls back to "{displayName} #{index + 1}" when no meaningful data is found.
 */
const getInstanceLabel = (instance, displayColumns, index, displayName) => {
  const fallback = `${displayName} #${index + 1}`;
  if (!instance || !displayColumns || displayColumns.length === 0) return fallback;

  for (const field of displayColumns) {
    if (!field || !field.id) continue;
    const value = instance[field.id];
    if (value !== null && value !== undefined && value !== '') {
      const stringValue = String(value).trim();
      if (stringValue.length > 0 && stringValue !== 'false') {
        const label = stringValue.length > 60 ? `${stringValue.substring(0, 60)}...` : stringValue;
        return label;
      }
    }
  }

  return fallback;
};

/**
 * RepeatedFormTableDisplay - Displays repeated form instances in a table with actions
 */
export const RepeatedFormTableDisplay = ({
  instances = [],
  subformSchema,
  fieldId,
  fieldLabel,
  minInstances = 0,
  maxInstances = 10,
  onChange,
  errors,
  formData,
  onBeforeNavigate,
  repeatedFormSchemaId,
  allowLinkExisting = true,
  allowCreateNew = true
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { formId } = useParams();
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [showLinkPicker, setShowLinkPicker] = useState(false);

  // Get first 3-5 fields for display columns (exclude headers, dividers)
  const displayColumns = (subformSchema || [])
    .filter(field => field && field.type !== 'header' && field.type !== 'divider')
    .slice(0, 5);

  const handleNavigateToSubform = (mode, instanceIndex = null) => {
    const instanceData = mode === 'edit' && instanceIndex !== null
      ? instances[instanceIndex]
      : {};

    // Get sessionGuid from current URL
    const pathParts = window.location.pathname.split('/');
    const sessionGuid = pathParts[pathParts.indexOf('create') + 1];

    // CRITICAL: Save draft immediately before navigating to prevent data loss
    if (onBeforeNavigate && formData && sessionGuid) {
      console.log('💾 Saving draft before navigating to subform...');
      onBeforeNavigate(sessionGuid, formData);
    }

    // Build subform URL with sessionGuid
    const subformUrl = `/forms/${formId}/submissions/create/${sessionGuid}/subform/${fieldId}/${instanceIndex !== null ? instanceIndex : ''}`;

    navigate(subformUrl, {
      state: {
        subformSchema,
        instanceData,
        instanceIndex,
        fieldId,
        fieldLabel,
        minInstances,
        maxInstances,
        currentInstanceCount: instances.length,
        returnUrl: window.location.pathname
      }
    });
  };

  const handleDelete = (index) => {
    if (instances.length <= minInstances) {
      toast.error(`Minimum ${minInstances} instance(s) required`);
      return;
    }

    setDeleteConfirmIndex(index);
  };

  const confirmDelete = () => {
    const newInstances = instances.filter((_, i) => i !== deleteConfirmIndex);
    onChange(newInstances);
    toast.success('Instance deleted');
    setDeleteConfirmIndex(null);
  };

  const handleDuplicate = (index) => {
    if (instances.length >= maxInstances) {
      toast.error(`Maximum ${maxInstances} instances allowed`);
      return;
    }

    // Deep copy to avoid reference issues
    const duplicatedInstance = JSON.parse(JSON.stringify(instances[index]));
    const newInstances = [...instances, duplicatedInstance];
    onChange(newInstances);
    toast.success('Instance duplicated');
  };

  const handleReorder = (index, direction) => {
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= instances.length) {
      return;
    }

    const newInstances = [...instances];
    [newInstances[index], newInstances[newIndex]] = [newInstances[newIndex], newInstances[index]];
    onChange(newInstances);
  };

  const handleLinkSelected = (selectedSubmissions) => {
    const linkedInstances = selectedSubmissions.map(sub => ({
      __mode: 'link',
      submissionId: sub.id,
      __linkedSubmissionId: sub.id,
      __linkedSubmitterName: sub.submitterName,
      ...(sub.submissionData?.fields || {})
    }));
    const newInstances = [...instances, ...linkedInstances];
    onChange(newInstances);
    setShowLinkPicker(false);
    toast.success(`Linked ${selectedSubmissions.length} submission${selectedSubmissions.length !== 1 ? 's' : ''}`);
  };

  const canAddMore = instances.length < maxInstances;
  const canRemove = instances.length > minInstances;

  return (
    <FieldContainer theme={theme}>
      <Label theme={theme}>
        {fieldLabel}
      </Label>

      {instances.length < minInstances && (
        <ErrorText theme={theme}>
          Minimum {minInstances} instance{minInstances !== 1 ? 's' : ''} required
        </ErrorText>
      )}

      <TableContainer theme={theme}>
        {instances.length === 0 ? (
          <EmptyState theme={theme}>
            <EmptyIcon><Inbox size={48} /></EmptyIcon>
            <ThemedText style={{ color: theme.colors.muted }}>
              No instances added yet. Click "Add {fieldLabel}" to get started.
            </ThemedText>
          </EmptyState>
        ) : (
          <Table>
            <TableHeader theme={theme}>
              <tr>
                <HeaderCell theme={theme}>#</HeaderCell>
                {displayColumns.map(field => (
                  <HeaderCell key={field.id} theme={theme}>
                    {field.props?.label || field.id}
                  </HeaderCell>
                ))}
                <HeaderCell theme={theme}>Actions</HeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {instances.map((instance, index) => {
                const isLinked = instance.__mode === 'link';
                return (
                <TableRow key={index} theme={theme}>
                  <TableCell theme={theme}>
                    {index + 1}
                    {isLinked && <LinkedBadge theme={theme}>Linked</LinkedBadge>}
                  </TableCell>
                  {displayColumns.map(field => (
                    <TableCell key={field.id} theme={theme}>
                      {renderFieldValue(instance, field)}
                    </TableCell>
                  ))}
                  <TableCell theme={theme}>
                    <ActionButtons theme={theme}>
                      {!isLinked && (
                      <IconButton
                        onClick={() => handleNavigateToSubform('edit', index)}
                        title="Edit"
                        theme={theme}
                      >
                        <Pencil size={18} />
                      </IconButton>
                      )}
                      {!isLinked && (
                      <IconButton
                        onClick={() => handleDuplicate(index)}
                        title="Duplicate"
                        theme={theme}
                        disabled={!canAddMore}
                      >
                        <Copy size={18} />
                      </IconButton>
                      )}
                      <IconButton
                        onClick={() => handleReorder(index, -1)}
                        disabled={index === 0}
                        title="Move Up"
                        theme={theme}
                      >
                        <ArrowUp size={18} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleReorder(index, 1)}
                        disabled={index === instances.length - 1}
                        title="Move Down"
                        theme={theme}
                      >
                        <ArrowDown size={18} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(index)}
                        disabled={!canRemove}
                        variant="danger"
                        title="Delete"
                        theme={theme}
                      >
                        <Trash2 size={18} />
                      </IconButton>
                    </ActionButtons>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <ButtonRow theme={theme}>
        {allowCreateNew && (
          <AddInstanceButton
            variant="secondary"
            onClick={() => handleNavigateToSubform('add')}
            disabled={!canAddMore}
            theme={theme}
          >
            <Plus size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Add {fieldLabel} {!canAddMore && `(Max: ${maxInstances})`}
          </AddInstanceButton>
        )}
        {allowLinkExisting && repeatedFormSchemaId && (
          <Button
            variant="secondary"
            onClick={() => setShowLinkPicker(true)}
            disabled={!canAddMore}
            style={{ whiteSpace: 'nowrap' }}
          >
            <LinkIcon size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />Link existing
          </Button>
        )}
      </ButtonRow>

      <InstanceCounter theme={theme}>
        {instances.length} of {maxInstances} instances ({minInstances} minimum required)
      </InstanceCounter>

      {errors && typeof errors === 'string' && (
        <ErrorText theme={theme}>{errors}</ErrorText>
      )}

      <ConfirmDialog
        isOpen={deleteConfirmIndex !== null}
        title="Confirm Delete"
        message="Are you sure you want to delete this instance?"
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirmIndex(null)}
      />

      {showLinkPicker && (
        <LinkExistingSubmissionPicker
          isOpen={showLinkPicker}
          onClose={() => setShowLinkPicker(false)}
          schemaId={repeatedFormSchemaId}
          schemaName={fieldLabel}
          existingChildIds={instances
            .filter(i => i.__mode === 'link' && i.submissionId)
            .map(i => i.submissionId)}
          onSelect={handleLinkSelected}
        />
      )}
    </FieldContainer>
  );
};
